import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';

/**
 * Editor for `public.incremental_config` (revenue completion paliers).
 *
 * One row per (server, period). Editable fields:
 *   - target_100pct (bigint)
 *   - completion_tiers (jsonb array of { pct, bonus })
 *
 * UX:
 *   - Period picker (YYYY-MM)
 *   - Loads or creates the row
 *   - Edit target_100pct + the tiers list (add/remove rows)
 *   - One Save button for the whole row.
 */

const monthInputValue = (period) => {
  // period is 'YYYY-MM' (no day) — but native <input type="month"> accepts 'YYYY-MM' directly
  return period || '';
};

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const IncrementalTab = ({ server }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(currentPeriod());
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    if (!server || !period) return;
    setLoading(true);
    setError(null);
    setSavedAt(null);
    const { data, error: dbError } = await supabase
      .from('incremental_config')
      .select('*')
      .eq('server', server)
      .eq('period', period)
      .maybeSingle();
    if (dbError) setError(dbError.message);
    setRow(
      data || {
        server,
        period,
        target_100pct: null,
        completion_tiers: [],
        _new: true,
      }
    );
    setLoading(false);
  }, [server, period]);

  useEffect(() => {
    load();
  }, [load]);

  const tiers = row?.completion_tiers || [];

  const updateTier = (idx, field, value) => {
    const next = [...tiers];
    next[idx] = { ...next[idx], [field]: value === '' ? null : Number(value) };
    setRow({ ...row, completion_tiers: next });
  };

  const removeTier = (idx) => {
    const next = tiers.filter((_, i) => i !== idx);
    setRow({ ...row, completion_tiers: next });
  };

  const addTier = () => {
    setRow({
      ...row,
      completion_tiers: [...tiers, { pct: 0, bonus: 0 }],
    });
  };

  const sortedTiers = useMemo(
    () =>
      [...tiers]
        .map((t, i) => ({ ...t, _origIdx: i }))
        .sort((a, b) => (a.pct ?? 0) - (b.pct ?? 0)),
    [tiers]
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSavedAt(null);
    const cleanTiers = (row.completion_tiers || [])
      .filter((t) => t.pct != null && t.bonus != null)
      .sort((a, b) => Number(a.pct) - Number(b.pct))
      .map((t) => ({ pct: Number(t.pct), bonus: Number(t.bonus) }));

    const payload = {
      server,
      period,
      target_100pct: row.target_100pct === '' ? null : row.target_100pct,
      completion_tiers: cleanTiers,
    };

    // Use upsert with conflict on (server, period)
    const { error: dbError } = await supabase
      .from('incremental_config')
      .upsert(payload, { onConflict: 'server,period' });

    if (dbError) {
      setError(dbError.message);
    } else {
      setSavedAt(Date.now());
    }
    setSaving(false);
    await load();
  };

  const inputBase =
    'w-full px-3 py-2 rounded-md bg-card border border-border text-foreground text-sm outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 disabled:opacity-60';

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(
          'superadmin.businessRules.incremental.intro',
          'Revenue completion paliers per server × period. Set the absolute diamond target for 100% and the % completion → bonus % map.'
        )}
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
          {t('superadmin.businessRules.incremental.period', 'Period')}
        </label>
        <input
          type="month"
          value={monthInputValue(period)}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-3 py-2 rounded-xl bg-card border border-border text-foreground text-base outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
        />
      </div>

      {error && (
        <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          <div className="h-12 rounded-xl skeleton" />
          <div className="h-32 rounded-xl skeleton" />
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border p-5 space-y-5">
          {/*
            Note: target_100pct is per-agency (not server-level) — set in the agency
            admin view at the start of each month. Hidden here to avoid confusion.
            The completion_tiers array (paliers en %) IS server-level and editable below.
          */}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                {t(
                  'superadmin.businessRules.incremental.tiers',
                  'Completion tiers'
                )}
              </label>
              <button
                type="button"
                onClick={addTier}
                className="px-3 py-1 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"
              >
                + {t('superadmin.businessRules.incremental.addTier', 'Add tier')}
              </button>
            </div>
            {sortedTiers.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                {t(
                  'superadmin.businessRules.incremental.noTiers',
                  'No tiers yet. Add at least one (pct → bonus) pair.'
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="hidden md:grid grid-cols-[1fr_1fr_auto] gap-3 px-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  <span>
                    {t(
                      'superadmin.businessRules.incremental.pct',
                      'Completion %'
                    )}
                  </span>
                  <span>
                    {t('superadmin.businessRules.incremental.bonus', 'Bonus %')}
                  </span>
                  <span />
                </div>
                {sortedTiers.map((tier) => (
                  <div
                    key={tier._origIdx}
                    className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-center"
                  >
                    <input
                      type="number"
                      step="any"
                      value={tier.pct ?? ''}
                      onChange={(e) =>
                        updateTier(tier._origIdx, 'pct', e.target.value)
                      }
                      placeholder="70"
                      className={`${inputBase} tabular-nums`}
                    />
                    <input
                      type="number"
                      step="any"
                      value={tier.bonus ?? ''}
                      onChange={(e) =>
                        updateTier(tier._origIdx, 'bonus', e.target.value)
                      }
                      placeholder="2"
                      className={`${inputBase} tabular-nums`}
                    />
                    <button
                      type="button"
                      onClick={() => removeTier(tier._origIdx)}
                      className="px-3 py-2 text-sm rounded-md text-error hover:bg-error/10 transition-colors"
                      aria-label="Remove tier"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
            <div className="text-xs text-muted-foreground">
              {savedAt &&
                t('superadmin.savedAt', 'Saved {{time}}', {
                  time: new Date(savedAt).toLocaleTimeString(),
                })}
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-accent/90 disabled:opacity-50 transition-colors"
            >
              {saving
                ? t('superadmin.businessRules.saving', 'Saving…')
                : t('superadmin.businessRules.save', 'Save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncrementalTab;
